import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { UserCheck } from "lucide-react";
import { toast } from "sonner";

/**
 * Admin "Complete Form as Contractor" launcher. Pops a small dialog that
 * lets the admin pick which contractor's identity they're proxy-filing
 * the Daily Note + eMAR under, then navigates to the proxy entry page.
 *
 * Audit stamping + signature locking is handled downstream — this
 * component is just the launcher.
 */
export function ProxyEntryButton({ clientId, contractorId }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [providers, setProviders] = useState([]);
  const [chosen, setChosen] = useState("");

  useEffect(() => {
    if (!open) return;
    api.get("/users/providers").then(({ data }) => {
      // Show the contractor assigned to this client, plus any other active providers (in case of multi-account contractor)
      const candidates = data.filter((p) => !p.revoked && (p.id === contractorId));
      setProviders(candidates.length ? candidates : data.filter((p) => !p.revoked));
      if (candidates[0]) setChosen(candidates[0].id);
    });
  }, [open, contractorId]);

  const go = (route) => {
    if (!chosen) return toast.error("Select the contractor whose workflow you're completing");
    navigate(`${route}&client=${clientId}&provider_id=${chosen}&proxy=1`);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" data-testid="open-proxy-entry">
          <UserCheck className="w-4 h-4 mr-2" /> Complete Form as Contractor
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Administrative Proxy Entry</DialogTitle>
          <DialogDescription>
            Choose which Contractor&apos;s workflow you&apos;re completing on behalf of.
            All submissions will be saved with an immutable audit stamp; signature canvases
            and &apos;I Attest&apos; checkboxes are hard-locked for admin users.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label className="text-label">Contractor</Label>
          <Select value={chosen} onValueChange={setChosen}>
            <SelectTrigger data-testid="proxy-provider-select"><SelectValue placeholder="Pick contractor" /></SelectTrigger>
            <SelectContent>
              {providers.length === 0 && <SelectItem value="__none" disabled>No active contractor</SelectItem>}
              {providers.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} ({p.email})</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <DialogFooter className="gap-2 flex-wrap">
          <Button variant="outline" onClick={() => go(`/admin/provider-proxy?_=1`)} data-testid="proxy-daily-note">
            Daily Note + eMAR
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
